import {
  Button,
  Checkbox,
  Condition,
  Form,
  FormRow,
  Option,
  PopupHint,
  RadioGroup,
  Select,
  ValidInput,
  ValidInputProps,
} from "@op/opds.opux";
import { ChangeEvent, FormEvent, useCallback, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useReferenceNumbers } from "../../providers/ReferenceNumberProvider";
import { amountOptions, defaultAmount, typeOptions } from "../constants";

const hint = (
  minLength: number,
  maxLength: number,
  type: "fi" | "rf",
  fiBase: boolean
) => (
  <PopupHint>
    <FormattedMessage
      id={
        fiBase || type === "fi"
          ? "referenceNumberHint"
          : "referenceNumberHintWithZero"
      }
      values={{ minLength, maxLength }}
    />
  </PopupHint>
);
const minLengthFi = 3;
const maxLengthFi = 19;
const minLengthRF = 1;
const maxLengthRF = 21;

type ErrorWithCode = { code: string | undefined } & Error;

const ReferenceNumberCalculatorForm = () => {
  const { generateReferenceNumbers } = useReferenceNumbers();
  const [inputError, setInputError] = useState<string | Error | true | null>(
    null
  );
  const [basePart, setBasePart] = useState("");
  const [amount, setAmount] = useState(defaultAmount);
  const [type, setType] = useState<"fi" | "rf">("fi");
  const inputRef = useRef<HTMLInputElement>(null);
  const [fiBase, setFiBase] = useState(true);
  const minLength = fiBase || type === "fi" ? minLengthFi : minLengthRF;
  const maxLength = fiBase || type === "fi" ? maxLengthFi : maxLengthRF;

  const { formatMessage } = useIntl();

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isValidBase(basePart) instanceof Error) {
      setInputError(isValidBase(basePart));
      inputRef.current?.focus();
      return;
    }
    await generateReferenceNumbers(basePart, amount, type, fiBase);
  };

  const isValidBase = useCallback(
    (str: string) => {
      if (str.length < minLength || str.length > maxLength) {
        return new Error(
          formatMessage(
            { id: "referenceErrorLength" },
            {
              minLength,
              maxLength,
            }
          )
        ) as ErrorWithCode;
      }

      if ((type === "fi" || fiBase) && str.startsWith("0")) {
        return new Error(
          formatMessage({ id: "referenceErrorFormat" })
        ) as ErrorWithCode;
      }
      if (type === "rf" && !fiBase && !/^[A-Za-z0-9]+$/.test(str)) {
        return new Error(
          formatMessage({ id: "referenceErrorRFChars" })
        ) as ErrorWithCode;
      }

      if ((type === "fi" || fiBase) && !/^\d+$/.test(str)) {
        return new Error(
          formatMessage({ id: "referenceErrorChars" })
        ) as ErrorWithCode;
      }

      return str;
    },
    [formatMessage, fiBase, type, minLength, maxLength]
  );

  const handleBasePartChange: ValidInputProps["onChange"] = (
    _event,
    value,
    error
  ) => {
    setInputError(error);
    setBasePart(value);
  };

  const handleTypeChange = (
    _event: FormEvent<HTMLInputElement>,
    value: string
  ) => {
    setType(value as typeof type);
  };

  const handleAmountChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setAmount(parseInt(event.target.value));
  };

  const referenceNumberBaseLabel = formatMessage({ id: "referenceNumberBase" });
  const hintText = hint(minLength, maxLength, type, fiBase);

  return (
    <Form
      onSubmit={handleSubmit}
      className="reference-number-form"
    >
      <FormRow
        label={<FormattedMessage id="referenceNumberType"></FormattedMessage>}
      >
        <RadioGroup
          value={type}
          onChange={handleTypeChange}
        >
          {typeOptions.map((value) => (
            <Option
              key={"type-" + value}
              value={value}
            >
              <FormattedMessage id={"type" + value} />
            </Option>
          ))}
        </RadioGroup>
      </FormRow>
      <FormRow
        label={referenceNumberBaseLabel}
        hint={hintText}
      >
        <ValidInput
          type="text"
          className="input"
          error={inputError}
          onChange={handleBasePartChange}
          validate={isValidBase}
          value={basePart}
          inputRef={inputRef}
        ></ValidInput>
      </FormRow>
      <FormRow label={<FormattedMessage id="amount" />}>
        <Select
          value={amount}
          className="input"
          onChange={handleAmountChange}
        >
          {amountOptions.map((value) => (
            <option
              key={value}
              value={value}
            >
              {value}
            </option>
          ))}
        </Select>
      </FormRow>
      <Condition
        value={type === "rf"}
        renderIfValueEquals={true}
      >
        <FormRow>
          <Checkbox
            checked={fiBase}
            onChange={(_e, checked) => {
              setFiBase(checked);
            }}
          >
            <FormattedMessage id="useFiBase" />
          </Checkbox>
        </FormRow>
      </Condition>
      <div className="button-row">
        <div className="spacer"></div>
        <Button
          type="submit"
          formNoValidate
        >
          <FormattedMessage id="createReferenceNumbers" />
        </Button>
      </div>
    </Form>
  );
};

export default ReferenceNumberCalculatorForm;
